import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from "react";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUp from "@mui/icons-material/KeyboardArrowUp";
import { Box, IconButton, Typography, useTheme } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import { alpha } from "@mui/material/styles";

import type { TimeOfDay } from "@thalamos/common";
import { END_OF_DAY, parseTimeOfDay, TimeOfDaySchema } from "@thalamos/common";

import type {
  AvailabilityBlock,
  AvailabilityGridPoint,
  CellRef,
} from "./availabilityGridModel.js";
import {
  blocksToChunkKeys,
  buildRowStarts,
  chunkKey,
  chunkKeySetsMatch,
  chunkKeysToBlocks,
  findRunAt,
  formatClockLabel,
  formatTimeRange,
  START_OF_DAY,
} from "./availabilityGridModel.js";

/** One column of the grid — a weekday, a specific date, or whatever the caller means by it. */
export interface AvailabilityGridColumn {
  /** Stable identity, used for React keys and test ids. */
  key: string;
  /** Primary heading, e.g. `"Sun"`. */
  label: string;
  /** Optional second heading line, e.g. `"12 June"`. */
  subLabel?: string;
  /** Draws the column as the one of current interest, e.g. today. */
  highlighted?: boolean;
}

export interface AvailabilityGridProps {
  columns: AvailabilityGridColumn[];
  /** Current availability. Rendered as given, so the caller owns the state. */
  value: AvailabilityBlock[];
  /** Called once per completed edit, with the whole new value. */
  onChange: (value: AvailabilityBlock[]) => void;
  /** Where the axis begins. */
  dayFrom?: TimeOfDay;
  /** Where the axis ends; `END_OF_DAY` to run to midnight. */
  dayUntil?: TimeOfDay;
  /**
   * Time to bring to the top of the axis on opening, clamped into the axis. The
   * default axis covers the whole day, most of which is not worth opening on.
   */
  initialScrollTo?: TimeOfDay;
  /**
   * How much time one cell covers. A cell is drawn only when it falls wholly
   * inside a block, so a `value` finer than this renders short and is rounded
   * to whole cells by the first edit — render existing availability at the
   * granularity it was captured at, never coarser.
   */
  granularityMinutes?: number;
  /**
   * Times to draw attention to, e.g. a range a caller has judged unusable.
   * Presentation alone: a flagged cell is as editable and as selectable as any
   * other, and carries `data-flagged="true"` so a caller can find it.
   */
  flaggedBlocks?: AvailabilityBlock[];
  readOnly?: boolean;
  /**
   * How many hours of the axis are shown at once; the rest scrolls. Default
   * {@link DEFAULT_VISIBLE_HOURS}.
   */
  visibleHours?: number;
  /** Height of one hour of the axis, from which cell height is derived. */
  hourHeightPx?: number;
  /**
   * Slashes every cell before this point, over whatever fill it carries, e.g.
   * time that has already passed. A cell is shaded only once it has wholly
   * elapsed, so the cell the point falls inside is not. Shading is
   * presentation alone: a shaded cell remains as editable as any other.
   */
  shadeBefore?: AvailabilityGridPoint;
  "data-testid"?: string;
}

type DragMode = "paint" | "erase";

interface DragState {
  anchor: CellRef;
  cursor: CellRef;
  mode: DragMode;
}

const TIME_GUTTER_WIDTH_PX = 60;
const MINUTES_PER_HOUR = 60;
const RANGE_READOUT_HEIGHT_PX = 20;
/** Keeps the readout clear of the run's top edge without leaving the run. */
const RANGE_READOUT_INSET_PX = 2;
const SCROLL_STEP_HOURS = 3;
/** `PointerEvent.button` for the primary (usually left) button. */
const PRIMARY_POINTER_BUTTON = 0;

/**
 * A working morning into early afternoon without scrolling, while leaving the
 * rest of the page visible on a laptop screen.
 */
const DEFAULT_VISIBLE_HOURS = 6;

const DEFAULT_INITIAL_SCROLL_TO = TimeOfDaySchema.parse("08:00:00");

/**
 * The edges of a flagged cell, drawn so a run of flagged cells reads as one
 * outlined box: each cell paints its own sides, and the edge it shares with a
 * flagged neighbour is left out. Inset shadows rather than an outline, which
 * the focus ring would replace on the focused cell.
 */
function flaggedOutline(
  colour: string,
  edges: { top: boolean; bottom: boolean },
): string {
  const shadows = [`inset 1px 0 0 0 ${colour}`, `inset -1px 0 0 0 ${colour}`];
  if (edges.top) {
    shadows.push(`inset 0 1px 0 0 ${colour}`);
  }
  if (edges.bottom) {
    shadows.push(`inset 0 -1px 0 0 ${colour}`);
  }
  return shadows.join(", ");
}

/**
 * The grid's fills, shared so surrounding chrome such as a legend cannot drift
 * from what the cells actually render.
 */
export function availabilityGridFills(theme: Theme) {
  return {
    available: alpha(theme.palette.success.main, 0.45),
    availableEmphasised: alpha(theme.palette.success.main, 0.62),
    availableHover: alpha(theme.palette.success.main, 0.18),
    // One diagonal stroke per cell rather than a wash or a texture: a wash sits
    // under a fill and vanishes, and a repeating texture restarts at every
    // cell's fractional-pixel edge. A single gradient sized to the cell has
    // nothing to tile, and a shape reads without colour perception. The corner
    // keyword runs the stroke from the cell's top-left to its bottom-right, and
    // the half-pixel ramps at its edges are what anti-alias it: a hard stop on a
    // diagonal is drawn jagged.
    pastSlash: `linear-gradient(to top right, transparent calc(50% - 1.25px), ${alpha(theme.palette.common.blueGray, 0.25)} calc(50% - 0.75px) calc(50% + 0.75px), transparent calc(50% + 1.25px))`,
  };
}

/**
 * Composes one cell's background: its fill, with the past slash over it when
 * shaded. The slash multiplies with the fill so it darkens the tile in the
 * tile's own hue rather than sitting on it as a flat grey.
 */
function composeCellBackground(
  fill: string | undefined,
  options: { shaded: boolean; slash: string },
): {
  backgroundColor: string;
  backgroundImage: string;
  backgroundBlendMode: string;
} {
  return {
    backgroundColor: fill ?? "transparent",
    backgroundImage: options.shaded ? options.slash : "none",
    backgroundBlendMode: "multiply",
  };
}

function cellDomKey(cell: CellRef): string {
  return `${String(cell.columnIndex)}:${String(cell.rowIndex)}`;
}

function readCellFrom(node: EventTarget | null): CellRef | undefined {
  if (!(node instanceof Element)) {
    return undefined;
  }
  const cellElement = node.closest("[data-column-index]");
  if (!(cellElement instanceof HTMLElement)) {
    return undefined;
  }
  const { columnIndex, rowIndex } = cellElement.dataset;
  if (columnIndex === undefined || rowIndex === undefined) {
    return undefined;
  }
  return { columnIndex: Number(columnIndex), rowIndex: Number(rowIndex) };
}

/**
 * The cell under the pointer. Found by position rather than by event target
 * because a touch pointer is implicitly captured by the element it pressed, so
 * its `pointermove` events keep naming that element wherever the finger goes.
 */
function readCellUnderPointer(
  event: ReactPointerEvent<HTMLDivElement>,
): CellRef | undefined {
  return readCellFrom(document.elementFromPoint(event.clientX, event.clientY));
}

/**
 * A weekly availability grid painted at arbitrary granularity — click or drag to
 * offer time, drag across time already offered to take it back.
 *
 * Deliberately knows nothing about doctors, rotas or dates: columns arrive
 * pre-labelled and availability is expressed as clock-time blocks, so one grid
 * serves both a repeating week and a specific dated one.
 */
export function AvailabilityGrid({
  columns,
  value,
  onChange,
  dayFrom = START_OF_DAY,
  dayUntil = END_OF_DAY,
  initialScrollTo = DEFAULT_INITIAL_SCROLL_TO,
  granularityMinutes = 15,
  flaggedBlocks = [],
  readOnly = false,
  visibleHours = DEFAULT_VISIBLE_HOURS,
  hourHeightPx = 56,
  shadeBefore,
  "data-testid": dataTestId = "availability-grid",
}: AvailabilityGridProps) {
  // A grid of no columns has no meaningful width to divide up and nothing to
  // navigate, so it is a caller error rather than an empty state to render.
  // Checked before any hook runs, so a failing render calls none of them.
  if (columns.length === 0) {
    throw new Error("AvailabilityGrid needs at least one column");
  }

  const theme = useTheme();

  const rowStarts = useMemo(
    () => buildRowStarts(dayFrom, dayUntil, granularityMinutes),
    [dayFrom, dayUntil, granularityMinutes],
  );
  const rowHeightPx = (hourHeightPx * granularityMinutes) / MINUTES_PER_HOUR;

  const baseKeys = useMemo(
    () =>
      blocksToChunkKeys(value, rowStarts, granularityMinutes, columns.length),
    [value, rowStarts, granularityMinutes, columns.length],
  );

  const flaggedKeys = useMemo(
    () =>
      blocksToChunkKeys(
        flaggedBlocks,
        rowStarts,
        granularityMinutes,
        columns.length,
      ),
    [flaggedBlocks, rowStarts, granularityMinutes, columns.length],
  );

  const [drag, setDrag] = useState<DragState | undefined>(undefined);
  const [hoverCell, setHoverCell] = useState<CellRef | undefined>(undefined);
  const [focusCell, setFocusCell] = useState<CellRef>({
    columnIndex: 0,
    rowIndex: 0,
  });
  const [scrollState, setScrollState] = useState({
    canScrollUp: false,
    canScrollDown: false,
  });

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const cellRefs = useRef(new Map<string, HTMLElement>());

  const headerRef = useRef<HTMLDivElement | null>(null);
  const [headerHeightPx, setHeaderHeightPx] = useState(0);
  // The header sits inside the scroll container, so the visible window is the
  // header's own height plus the requested number of hours.
  useLayoutEffect(() => {
    setHeaderHeightPx(headerRef.current?.offsetHeight ?? 0);
  }, [columns]);
  const bodyHeightPx = headerHeightPx + visibleHours * hourHeightPx;

  /** `baseKeys` with the in-flight drag rectangle applied — i.e. what is on screen. */
  const draftKeys = useMemo(() => {
    if (!drag) {
      return baseKeys;
    }
    const next = new Set(baseKeys);
    const firstColumn = Math.min(
      drag.anchor.columnIndex,
      drag.cursor.columnIndex,
    );
    const lastColumn = Math.max(
      drag.anchor.columnIndex,
      drag.cursor.columnIndex,
    );
    const firstRow = Math.min(drag.anchor.rowIndex, drag.cursor.rowIndex);
    const lastRow = Math.max(drag.anchor.rowIndex, drag.cursor.rowIndex);

    for (
      let columnIndex = firstColumn;
      columnIndex <= lastColumn;
      columnIndex++
    ) {
      for (let rowIndex = firstRow; rowIndex <= lastRow; rowIndex++) {
        const rowStart = rowStarts[rowIndex];
        if (rowStart === undefined) {
          continue;
        }
        const key = chunkKey(columnIndex, rowStart);
        if (drag.mode === "paint") {
          next.add(key);
        } else {
          next.delete(key);
        }
      }
    }
    return next;
  }, [drag, baseKeys, rowStarts]);

  const commitKeys = useCallback(
    (nextKeys: ReadonlySet<string>) => {
      if (chunkKeySetsMatch(nextKeys, baseKeys)) {
        return;
      }
      onChange(
        chunkKeysToBlocks(
          nextKeys,
          rowStarts,
          columns.length,
          granularityMinutes,
        ),
      );
    },
    [baseKeys, onChange, rowStarts, columns.length, granularityMinutes],
  );

  const isEditable = useCallback(
    (cell: CellRef): boolean =>
      !readOnly && rowStarts[cell.rowIndex] !== undefined,
    [readOnly, rowStarts],
  );

  const isSelectedIn = useCallback(
    (keys: ReadonlySet<string>, cell: CellRef): boolean => {
      const rowStart = rowStarts[cell.rowIndex];
      return (
        rowStart !== undefined && keys.has(chunkKey(cell.columnIndex, rowStart))
      );
    },
    [rowStarts],
  );

  const focusCellElement = (cell: CellRef) => {
    setFocusCell(cell);
    cellRefs.current.get(cellDomKey(cell))?.focus();
  };

  const handlePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    // Secondary buttons belong to the context menu, not to painting.
    if (event.button !== PRIMARY_POINTER_BUTTON) {
      return;
    }
    const cell = readCellFrom(event.target);
    if (!cell || !isEditable(cell)) {
      return;
    }
    // Painting must not double as a text selection or a native element drag;
    // the cell is focused explicitly because that also suppresses default focus.
    event.preventDefault();
    focusCellElement(cell);
    setDrag({
      anchor: cell,
      cursor: cell,
      mode: isSelectedIn(baseKeys, cell) ? "erase" : "paint",
    });
  };

  const handlePointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const cell = readCellUnderPointer(event);
    setHoverCell(cell);
    if (!cell) {
      return;
    }
    if (
      drag &&
      (cell.columnIndex !== drag.cursor.columnIndex ||
        cell.rowIndex !== drag.cursor.rowIndex)
    ) {
      setDrag({ ...drag, cursor: cell });
    }
  };

  const endDrag = () => {
    if (!drag) {
      return;
    }
    commitKeys(draftKeys);
    setDrag(undefined);
  };

  const handlePointerLeave = () => {
    setHoverCell(undefined);
    endDrag();
  };

  const toggleCell = (cell: CellRef) => {
    const rowStart = rowStarts[cell.rowIndex];
    if (!isEditable(cell) || rowStart === undefined) {
      return;
    }
    const next = new Set(baseKeys);
    const key = chunkKey(cell.columnIndex, rowStart);
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    commitKeys(next);
  };

  /** Give `cell` whatever state `from` has, so shift-arrow paints like a drag. */
  const extendFrom = (from: CellRef, cell: CellRef) => {
    const rowStart = rowStarts[cell.rowIndex];
    if (!isEditable(cell) || rowStart === undefined) {
      return;
    }
    const next = new Set(baseKeys);
    const key = chunkKey(cell.columnIndex, rowStart);
    if (isSelectedIn(baseKeys, from)) {
      next.add(key);
    } else {
      next.delete(key);
    }
    commitKeys(next);
  };

  const handleKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    const lastColumnIndex = columns.length - 1;
    const lastRowIndex = rowStarts.length - 1;

    const step = (columnDelta: number, rowDelta: number) => {
      event.preventDefault();
      const next: CellRef = {
        columnIndex: Math.min(
          Math.max(focusCell.columnIndex + columnDelta, 0),
          lastColumnIndex,
        ),
        rowIndex: Math.min(
          Math.max(focusCell.rowIndex + rowDelta, 0),
          lastRowIndex,
        ),
      };
      if (event.shiftKey) {
        extendFrom(focusCell, next);
      }
      focusCellElement(next);
    };

    switch (event.key) {
      case "ArrowUp":
        step(0, -1);
        return;
      case "ArrowDown":
        step(0, 1);
        return;
      case "ArrowLeft":
        step(-1, 0);
        return;
      case "ArrowRight":
        step(1, 0);
        return;
      case "Home":
        event.preventDefault();
        focusCellElement({ columnIndex: focusCell.columnIndex, rowIndex: 0 });
        return;
      case "End":
        event.preventDefault();
        focusCellElement({
          columnIndex: focusCell.columnIndex,
          rowIndex: lastRowIndex,
        });
        return;
      case " ":
      case "Enter":
        event.preventDefault();
        toggleCell(focusCell);
        return;
      default:
        return;
    }
  };

  const measureScroll = useCallback(() => {
    const element = scrollRef.current;
    if (!element) {
      return;
    }
    const remainingBelow =
      element.scrollHeight - element.clientHeight - element.scrollTop;
    setScrollState({
      canScrollUp: element.scrollTop > 1,
      canScrollDown: remainingBelow > 1,
    });
  }, []);

  useEffect(() => {
    const element = scrollRef.current;
    if (!element) {
      return;
    }
    const target = parseTimeOfDay(initialScrollTo);
    const rowIndex = rowStarts.findIndex((rowStart) => rowStart >= target);
    // Rows begin below the sticky header, and the header covers the top of the
    // viewport, so the two cancel: scrolling by the row's offset within the rows
    // lands it immediately under the header rather than behind it.
    element.scrollTop = rowIndex < 0 ? 0 : rowIndex * rowHeightPx;
    measureScroll();
  }, [measureScroll, initialScrollTo, rowStarts, rowHeightPx, bodyHeightPx]);

  const scrollByHours = (hours: number) => {
    scrollRef.current?.scrollBy({
      top: hours * hourHeightPx,
      behavior: "smooth",
    });
  };

  /** The whole run under the pointer, which is what the readout describes. */
  const hoveredRun = useMemo(() => {
    if (!hoverCell || drag) {
      return undefined;
    }
    const run = findRunAt(draftKeys, hoverCell, rowStarts);
    if (!run) {
      return undefined;
    }
    const runStart = rowStarts[run.firstRowIndex] ?? 0;
    const runEnd = (rowStarts[run.lastRowIndex] ?? 0) + granularityMinutes;
    return {
      ...run,
      columnIndex: hoverCell.columnIndex,
      label: formatTimeRange(runStart, runEnd),
    };
  }, [hoverCell, drag, draftKeys, rowStarts, granularityMinutes]);

  // Minutes into `shadeBefore`'s own column that the shading reaches.
  const shadeUntilMinutes =
    shadeBefore === undefined ? undefined : parseTimeOfDay(shadeBefore.time);

  // The header and the body are separate grids, so no column may grow to fit
  // its content: a wide heading would then stand out of line with its cells.
  const gridTemplateColumns = `${String(TIME_GUTTER_WIDTH_PX)}px repeat(${String(columns.length)}, minmax(0, 1fr))`;
  const columnWidth = `calc((100% - ${String(TIME_GUTTER_WIDTH_PX)}px) / ${String(columns.length)})`;

  const fills = availabilityGridFills(theme);

  return (
    <Box data-testid={dataTestId} sx={{ position: "relative" }}>
      <Box
        ref={scrollRef}
        onScroll={measureScroll}
        sx={{
          maxHeight: `${String(bodyHeightPx)}px`,
          overflowY: "auto",
          // A finger drag over the grid paints cells, so the browser must not
          // also read it as a pan; the scroll affordances move the day instead.
          // Pinch zoom is left alone, being the gesture nobody paints with.
          touchAction: "pinch-zoom",
          // The scroll bar invites a finger to drag the day along, but a
          // finger drag paints, so on a touch screen only the affordances show.
          "@media (pointer: coarse)": {
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
          },
          border: `1px solid ${theme.palette.text.border}`,
          borderRadius: 1,
          backgroundColor: theme.palette.background.default,
        }}
      >
        <Box
          role="grid"
          aria-label="Availability"
          aria-readonly={readOnly}
          aria-multiselectable={!readOnly}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          onPointerLeave={handlePointerLeave}
          onKeyDown={handleKeyDown}
        >
          <Box
            ref={headerRef}
            role="rowgroup"
            sx={{
              display: "grid",
              gridTemplateColumns,
              position: "sticky",
              top: 0,
              zIndex: 2,
              backgroundColor: theme.palette.background.default,
              borderBottom: `1px solid ${theme.palette.text.border}`,
            }}
          >
            <Box role="row" sx={{ display: "contents" }}>
              <Box
                role="columnheader"
                aria-label="Time"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <ScrollAffordance
                  direction="up"
                  hidden={!scrollState.canScrollUp}
                  onClick={() => {
                    scrollByHours(-SCROLL_STEP_HOURS);
                  }}
                />
              </Box>
              {columns.map((column) => (
                <Box
                  key={column.key}
                  role="columnheader"
                  data-testid={`availability-grid-header-${column.key}`}
                  data-highlighted={
                    column.highlighted === true ? "true" : undefined
                  }
                  sx={{
                    py: 1,
                    overflow: "hidden",
                    textAlign: "center",
                    borderLeft: `1px solid ${theme.palette.common.lightGray}`,
                    backgroundColor:
                      column.highlighted === true
                        ? theme.palette.common.paleBlue
                        : "transparent",
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {column.label}
                  </Typography>
                  {column.subLabel !== undefined && (
                    <Typography
                      variant="caption"
                      component="div"
                      sx={{ color: theme.palette.text.secondary }}
                    >
                      {column.subLabel}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          </Box>

          <Box
            role="rowgroup"
            sx={{ display: "grid", gridTemplateColumns, position: "relative" }}
          >
            {rowStarts.map((rowStart, rowIndex) => {
              const isHourStart = rowStart % MINUTES_PER_HOUR === 0;
              return (
                <Box key={rowStart} role="row" sx={{ display: "contents" }}>
                  <Box
                    role="rowheader"
                    aria-label={formatClockLabel(rowStart)}
                    sx={{
                      height: `${String(rowHeightPx)}px`,
                      position: "relative",
                    }}
                  >
                    {isHourStart && (
                      <Typography
                        variant="caption"
                        aria-hidden
                        sx={{
                          position: "absolute",
                          right: theme.spacing(1),
                          top: 0,
                          // Labels straddle their hour's gridline, except the
                          // first, which would be cut off by the top of the axis.
                          transform:
                            rowIndex === 0 ? "none" : "translateY(-50%)",
                          color: theme.palette.text.secondary,
                          backgroundColor: theme.palette.background.default,
                          lineHeight: 1,
                        }}
                      >
                        {formatClockLabel(rowStart)}
                      </Typography>
                    )}
                  </Box>
                  {columns.map((column, columnIndex) => {
                    const cell: CellRef = { columnIndex, rowIndex };
                    const key = chunkKey(columnIndex, rowStart);
                    const selected = draftKeys.has(key);
                    const flagged = flaggedKeys.has(key);
                    const flaggedAbove = flaggedKeys.has(
                      chunkKey(columnIndex, rowStart - granularityMinutes),
                    );
                    const flaggedBelow = flaggedKeys.has(
                      chunkKey(columnIndex, rowStart + granularityMinutes),
                    );
                    const inHoveredRun =
                      hoveredRun !== undefined &&
                      hoveredRun.columnIndex === columnIndex &&
                      rowIndex >= hoveredRun.firstRowIndex &&
                      rowIndex <= hoveredRun.lastRowIndex;
                    const isFocusCell =
                      focusCell.columnIndex === columnIndex &&
                      focusCell.rowIndex === rowIndex;

                    const shaded =
                      shadeBefore !== undefined &&
                      shadeUntilMinutes !== undefined &&
                      (columnIndex < shadeBefore.columnIndex ||
                        (columnIndex === shadeBefore.columnIndex &&
                          rowStart + granularityMinutes <= shadeUntilMinutes));

                    const restingFill = selected
                      ? inHoveredRun
                        ? fills.availableEmphasised
                        : fills.available
                      : undefined;

                    return (
                      <Box
                        key={column.key}
                        ref={(element: HTMLElement | null) => {
                          if (element) {
                            cellRefs.current.set(cellDomKey(cell), element);
                          } else {
                            cellRefs.current.delete(cellDomKey(cell));
                          }
                        }}
                        role="gridcell"
                        aria-selected={selected}
                        aria-disabled={readOnly}
                        aria-label={`${column.label} ${formatTimeRange(rowStart, rowStart + granularityMinutes)} — ${selected ? "available" : "unavailable"}`}
                        tabIndex={isFocusCell ? 0 : -1}
                        data-column-index={columnIndex}
                        data-row-index={rowIndex}
                        data-selected={selected}
                        data-shaded={shaded ? "true" : undefined}
                        data-flagged={flagged ? "true" : undefined}
                        data-testid={`availability-cell-${column.key}-${formatClockLabel(rowStart)}`}
                        sx={{
                          height: `${String(rowHeightPx)}px`,
                          boxSizing: "border-box",
                          borderLeft: `1px solid ${theme.palette.common.lightGray}`,
                          borderTop: isHourStart
                            ? `1px solid ${theme.palette.common.lightGray}`
                            : `1px dashed ${alpha(theme.palette.text.border, 0.6)}`,
                          ...composeCellBackground(restingFill, {
                            shaded,
                            slash: fills.pastSlash,
                          }),
                          boxShadow: flagged
                            ? flaggedOutline(theme.palette.error.main, {
                                top: !flaggedAbove,
                                bottom: !flaggedBelow,
                              })
                            : undefined,
                          cursor: readOnly ? "default" : "pointer",
                          transition: "background-color 120ms",
                          outlineOffset: "-2px",
                          "&:focus-visible": {
                            outline: `2px solid ${theme.palette.primary.main}`,
                          },
                          // A pointer that cannot hover cannot un-hover: a
                          // tap would leave this tint stuck on the cell it
                          // landed on, where it reads as time already offered.
                          "@media (hover: hover)": {
                            "&:hover": readOnly
                              ? {}
                              : composeCellBackground(
                                  selected
                                    ? fills.availableEmphasised
                                    : fills.availableHover,
                                  { shaded, slash: fills.pastSlash },
                                ),
                          },
                        }}
                      />
                    );
                  })}
                </Box>
              );
            })}

            {hoveredRun && (
              <Box
                sx={{
                  position: "absolute",
                  // The readout sits over cells of its own run, which must
                  // stay clickable through it, so it never takes the pointer.
                  pointerEvents: "none",
                  left: `calc(${String(TIME_GUTTER_WIDTH_PX)}px + ${columnWidth} * ${String(hoveredRun.columnIndex)})`,
                  width: columnWidth,
                  top: `${String(hoveredRun.firstRowIndex * rowHeightPx)}px`,
                  zIndex: 1,
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    top: `${String(RANGE_READOUT_INSET_PX)}px`,
                    left: "50%",
                    transform: "translateX(-50%)",
                    display: "flex",
                    alignItems: "center",
                    px: 0.75,
                    height: `${String(RANGE_READOUT_HEIGHT_PX)}px`,
                    borderRadius: 1,
                    whiteSpace: "nowrap",
                    backgroundColor: theme.palette.text.primary,
                    color: theme.palette.common.white,
                    boxShadow: theme.shadows[2],
                  }}
                >
                  <Typography
                    variant="caption"
                    aria-hidden
                    data-testid="availability-grid-range-readout"
                    sx={{ lineHeight: 1 }}
                  >
                    {hoveredRun.label}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {scrollState.canScrollDown && (
        <Box
          sx={{
            position: "absolute",
            bottom: 1,
            left: 1,
            // Spans the gutter so it masks the whole hour label behind it,
            // rather than leaving a fragment showing around the control.
            width: `${String(TIME_GUTTER_WIDTH_PX)}px`,
            display: "flex",
            justifyContent: "center",
            backgroundColor: theme.palette.background.default,
          }}
        >
          <ScrollAffordance
            direction="down"
            onClick={() => {
              scrollByHours(SCROLL_STEP_HOURS);
            }}
          />
        </Box>
      )}
    </Box>
  );
}

/**
 * Signals — and reaches — time beyond the visible axis. Positioned by its caller
 * so the "up" control can sit inside the sticky header, where it can neither be
 * scrolled away nor painted over.
 *
 * Full-size touch targets: a finger cannot pan the axis, so these are the only
 * way through the day on a touch screen.
 */
function ScrollAffordance({
  direction,
  hidden = false,
  onClick,
}: {
  direction: "up" | "down";
  /**
   * Withdraws the control while keeping the space it occupies, for a caller
   * whose own layout is measured from it.
   */
  hidden?: boolean;
  onClick: () => void;
}) {
  const theme = useTheme();
  return (
    <IconButton
      aria-label={
        direction === "up" ? "Show earlier times" : "Show later times"
      }
      data-testid={`availability-grid-scroll-${direction}`}
      onClick={onClick}
      sx={{
        visibility: hidden ? "hidden" : undefined,
        color: theme.palette.text.secondary,
        // Opaque so it masks, rather than muddles, the hour label behind it.
        backgroundColor: theme.palette.background.default,
        "&:hover": { backgroundColor: theme.palette.background.lightGray },
      }}
    >
      {direction === "up" ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
    </IconButton>
  );
}
